import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { 
  FiHome,
  FiUsers,
  FiUserPlus,
  FiBriefcase,
  FiSettings,
  FiShield,
  FiDollarSign,
  FiBarChart,
  FiLogOut,
  FiChevronDown,
  FiMenu,
  FiX,
  FiEye,
  FiLock
} from 'react-icons/fi';

const AdminLayout = () => {
  const { currentUser, logout } = useAuth();
  const { 
    currentOrganization,
    isFullOrgAdmin,
    isLimitedOrgAdmin,
    isReadOnlyOrgAdmin 
  } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine actual admin access level
  // If no explicit access level is set, org_admin defaults to full access
  const effectiveFullOrgAdmin = isFullOrgAdmin || (!isLimitedOrgAdmin && !isReadOnlyOrgAdmin);
  const effectiveLimitedOrgAdmin = isLimitedOrgAdmin;
  const effectiveReadOnlyOrgAdmin = isReadOnlyOrgAdmin;

  // Build navigation items based on access level
  const buildAdminNavItems = () => {
    const items = [];
    
    // Dashboard - available to all admins
    items.push({
      label: 'Dashboard',
      path: '/org/admin',
      icon: FiHome,
      exact: true
    });
    
    // Members - available to all admins
    items.push({
      label: effectiveReadOnlyOrgAdmin ? 'View Members' : 'Members',
      path: '/org/admin/members',
      icon: FiUsers
    });
    
    // Invite - available to full and limited admins
    if (effectiveFullOrgAdmin || effectiveLimitedOrgAdmin) {
      items.push({
        label: 'Invite',
        path: '/org/admin/invite',
        icon: FiUserPlus
      });
    }
    
    // Departments - available to full and limited admins
    if (effectiveFullOrgAdmin || effectiveLimitedOrgAdmin) {
      items.push({
        label: 'Departments',
        path: '/org/admin/departments',
        icon: FiBriefcase
      });
    }
    
    // Analytics - available to all admins
    items.push({
      label: 'Analytics',
      path: '/org/admin/analytics',
      icon: FiBarChart
    });
    
    // Security - available to all admins (main purpose for security reviewers)
    items.push({
      label: 'Security',
      path: '/org/admin/security',
      icon: FiShield
    });
    
    // Settings - available to full and limited admins (limited may have restricted settings)
    if (effectiveFullOrgAdmin || effectiveLimitedOrgAdmin) {
      items.push({
        label: 'Settings',
        path: '/org/admin/settings',
        icon: FiSettings
      });
    }
    
    // Billing - only for full admins
    if (effectiveFullOrgAdmin) {
      items.push({
        label: 'Billing',
        path: '/org/admin/billing',
        icon: FiDollarSign
      });
    }
    
    return items;
  };

  const adminNavItems = buildAdminNavItems();

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const handleBackToOrg = () => {
    navigate('/org/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left Section - Logo & Org Name */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center shadow-lg">
                  <FiShield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">Admin Panel</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {currentOrganization?.name || 'Organization'}
                  </p>
                </div>
              </div>
            </div>

            {/* Center Section - Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path, item.exact);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200
                      ${active
                        ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 ${active ? 'text-purple-600' : ''}`} />
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Right Section - User Menu */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToOrg}
                className="hidden sm:flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <span>Back to Organization</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {currentUser?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {currentUser?.email}
                    </p>
                  </div>
                  <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {currentUser?.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {currentUser?.email}
                      </p>
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        {currentUser?.orgAdminAccessLevel === 'limited' ? 'Delegated Admin' : currentUser?.orgAdminAccessLevel === 'read_only' ? 'Security Reviewer' : 'Full Admin'}
                      </p>
                    </div>
                    <button
                      onClick={handleBackToOrg}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <FiHome className="w-4 h-4" />
                      <span>Back to Organization</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                    >
                      <FiLogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-700">
              <nav className="space-y-1">
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path, item.exact);
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`
                        flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                        ${active
                          ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      <Icon className={`w-5 h-5 ${active ? 'text-purple-600' : ''}`} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
